import { useEffect, useState } from "react";
import { SocialPulseGuide } from "../components/SocialPulseGuide";
import { useParams, Link } from "react-router-dom";
import {
  MapPin, CalendarDays, Landmark, Ticket, Plane, TrainFront, Bus, CarTaxiFront,
  Users, Car, Ship, Hotel, Stamp, BookOpen, Camera as CameraIcon, UtensilsCrossed,
  Trees, Waves, Building, Sparkles,
} from "lucide-react";
import { PageHeader, Spinner, Pill } from "../components/common";
import EntityHero from "../components/EntityHero";
import { pickAirportBg } from "../lib/assets";
import { DirectionsButton } from "../components/DirectionsButton";
import Breadcrumbs from "../components/Breadcrumbs";
import Weather from "../components/Weather";
import DiscussionBoard from "../components/DiscussionBoard";
import EntityStoriesFeed from "../components/EntityStoriesFeed";
import AiContentTabs from "../components/travel/AiContentTabs";
import { SITE } from "../lib/site";
import api from "../lib/api";
import SEOHead from "../components/SEOHead";
import { touristDestinationLd, breadcrumbLd, cleanLd } from "../lib/structuredData";

const TYPE_ICONS = {
  landmark: Landmark, museum: Building, historic: Landmark, park: Trees, beach: Waves,
  experience: Sparkles, food: UtensilsCrossed, neighborhood: MapPin,
};

export default function DestinationDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setD(null); setErr(false);
    api.get(`/destinations/${id}`).then((r) => {
      setD(r.data);
      document.title = `${r.data.name} Travel Guide — Attractions, Events & Transport | ${SITE.brandDomain}`;
    }).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [id]);

  if (err) return (
    <div className="max-w-7xl mx-auto px-5 py-20 text-center" data-testid="destination-not-found">
      <p className="text-muted-foreground">Destination guide not found.</p>
      <Link to="/destinations" className="text-primary font-700 hover:underline mt-2 inline-block">← All destinations</Link>
    </div>
  );
  if (!d) return <Spinner full />;

  const cityLink = `/cities/${encodeURIComponent(d.name)}?country=${d.iso2}`;
  const verticals = [
    { icon: TrainFront, label: "Rail & transit", to: "/rail" },
    { icon: Bus, label: "Bus & coach", to: "/bus" },
    { icon: CarTaxiFront, label: "Taxi & ground", to: "/ground" },
    { icon: Users, label: "Rideshare", to: "/rideshare" },
    { icon: Car, label: "Road & rentals", to: "/road" },
    { icon: Ship, label: "Water travel", to: "/water" },
    { icon: Hotel, label: "Places to stay", to: "/stays" },
    { icon: Stamp, label: `${d.country} visas & entry`, to: d.visa_country_page ? `/visas/${d.visa_country_page.id}` : "/visas" },
  ];

  return (
    <div data-testid={`destination-page-${d.id}`}>
      <SEOHead
        title={`${d.name} Travel Guide — Attractions, Events & Transport`}
        description={d.summary || d.tagline || `Plan your visit to ${d.name} with attractions, transport, visas and local community insight.`}
        canonical={`/destinations/${d.slug || d.id}`}
        image={d.cover || d.photo}
        type="article"
        jsonLd={[
          cleanLd(touristDestinationLd(d)),
          breadcrumbLd([["Home", "/"], ["Destinations", "/destinations"], [d.name]]),
        ].filter(Boolean)}
      />
      <EntityHero
        testid="destination-hero"
        image={pickAirportBg(d.id || d.name)}
        tone="cyan"
        breadcrumbs={<Breadcrumbs items={[
          { label: "Destinations", to: "/destinations" },
          { label: d.region, to: "/destinations" },
          { label: d.name },
        ]} />}
        tags={d.iso2 && <Pill tone="blue">{d.iso2}</Pill>}
        title={d.name}
        subtitle={<>{d.tagline}{d.description ? ` — ${d.description}` : ""}</>}
        actions={
          <>
            <span className="text-sm text-white/90 inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-md">
              <MapPin className="w-4 h-4" />{d.country}
            </span>
            <span className="text-sm text-white/90 inline-flex items-center gap-1.5 bg-white/10 backdrop-blur-sm px-3 py-2 rounded-md">
              <CalendarDays className="w-4 h-4" />Best time: {d.best_time}
            </span>
          </>
        }
        quickFacts={[
          { label: "Country",   value: d.country },
          { label: "Region",    value: d.region },
          { label: "Best time", value: d.best_time },
          { label: "Currency",  value: d.currency || d.currency_code },
        ]}
      />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-12">
        <div className="flex flex-wrap items-center gap-4">
          {d.lat != null && <Weather lat={d.lat} lon={d.lon} label={d.name} />}
          <Link to={cityLink} data-testid="destination-city-guide-link"
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2.5 font-600 text-sm hover:bg-blue-700 transition-colors">
            <BookOpen className="w-4 h-4" /> Full {d.name} city profile
          </Link>
        </div>

        {/* Attractions */}
        <section data-testid="destination-attractions">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <CameraIcon className="w-6 h-6 text-[#0e7490]" /> Things to see &amp; do
          </h2>
          <p className="text-sm text-muted-foreground mb-5">Signature attractions and experiences in {d.name}.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(d.attractions || []).map((at) => {
              const Icon = TYPE_ICONS[at.type] || Sparkles;
              return (
                <div key={at.name} className="border border-zinc-300 bg-white p-5" data-testid={`attraction-${at.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="p-1.5 bg-cyan-50 text-[#0e7490]"><Icon className="w-4 h-4" /></span>
                    <Pill tone="muted">{at.type}</Pill>
                    <DirectionsButton size="icon" name={`${at.name}, ${d.name}`} className="ml-auto"
                      testid={`attraction-directions-${at.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} />
                  </div>
                  <h3 className="font-heading font-700 text-base leading-snug">{at.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1.5">{at.blurb}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* AI-authored editorial sections (Cuisine · Culture · Neighbourhoods · Safety · Insider Tips)
            with 45+ language switcher and Listen-to-this narration */}
        <AiContentTabs entityType="destination" entityId={d.id} entityName={d.name} />

        {/* Events */}
        {d.events?.length > 0 && (
          <section data-testid="destination-events">
            <h2 className="font-heading font-900 text-2xl tracking-tight mb-5 flex items-center gap-2">
              <Ticket className="w-6 h-6 text-[#0e7490]" /> Signature events
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {d.events.map((ev) => (
                <div key={ev.name} className="border-l-4 border-[#F4C77B] border-y border-r border-y-zinc-300 border-r-zinc-300 bg-white p-5">
                  <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-1">{ev.month}</div>
                  <h3 className="font-heading font-700 text-base">{ev.name}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{ev.blurb}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Getting there & around — all 8 verticals */}
        <section data-testid="destination-transport">
          <h2 className="font-heading font-900 text-2xl tracking-tight mb-1 flex items-center gap-2">
            <Plane className="w-6 h-6 text-[#0e7490]" /> Getting there &amp; around
          </h2>
          <p className="text-sm text-zinc-700 max-w-3xl mb-5">{d.getting_around}</p>
          {d.airport_details?.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              {d.airport_details.map((ap) => (
                <Link key={ap.iata_code} to={`/airports/${ap.iata_code}`} data-testid={`destination-airport-${ap.iata_code}`}
                  className="flex items-center gap-3 border border-zinc-300 bg-white px-4 py-3 hover:border-[#0e7490] transition-colors">
                  <span className="font-mono font-700 text-[#0e7490]">{ap.iata_code}</span>
                  <div className="min-w-0">
                    <div className="text-sm font-600 truncate">{ap.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{(ap.type || "").replace("_", " ")}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="destination-verticals">
            {verticals.map((v) => (
              <Link key={v.label} to={v.to}
                className="flex items-center gap-2.5 border border-zinc-300 bg-white px-4 py-3 hover:border-[#0e7490] hover:-translate-y-px transition-[transform,border-color]">
                <v.icon className="w-5 h-5 text-[#0e7490] shrink-0" />
                <span className="text-sm font-600">{v.label}</span>
              </Link>
            ))}
          </div>
        </section>

        {/* SocialPulse / AI city guide */}
        <SocialPulseGuide country={d.country} city={d.name} />

        {/* Latest from XYZ Travellers */}
        <EntityStoriesFeed entityType="destination" entityId={d.id} entityName={d.name} />

        {/* Community */}
        <DiscussionBoard entityType="destination" entityId={d.id} title={`${d.name} traveler community`} />
      </div>
    </div>
  );
}
